import type { BranchFilters, BranchShowVO, RouterIdsVO } from "@/api"
import { getBrandStoreCount, getStoreList, IndustryCategory } from "@/api"
import {
  polygonHeatColors,
  showZoomMap,
  SHOW_ADVERTISEMENT_TEXT,
  SHOW_ICON_TEXT,
  storeLimit,
  viewZoom,
  TYPE_CUSTOM_POI,
  TYPE_WAYZ_POI,
  SHOW_SINGLE_STORE_ZOOM,
} from "@/constants/constant"
import { isNH } from "@/constants/NHConfig"
import {
  useBrandPoiModel,
  useConfigModel,
  useControllerModel,
  useIndicatorModel,
  useQuotaToolsModel,
  useStoreListModel,
} from "@/models"
import { getBranchThemeColors, getCenterOffset, getCurrentPageType } from "@/util/commonUtils"
import { colorToRGBArray } from "@/util/util"
import { GeoJsonLayer, useMapGLContext } from "@wayz/react-gl"
import { useCreation, usePersistFn, useRequest, useSetState } from "ahooks-v2"
import { scaleQuantize } from "d3-scale"
import GeoJSON from "geojson"
import type { FC } from "react"
import { memo, useCallback, useEffect } from "react"
import { unstable_batchedUpdates } from "react-dom"
import { useLocation, useParams } from "react-router-dom"
import { useMapValue } from "../MapContainer/context"
// =================================================================
import AreaHeat from "./components/AreaHeat"
import ClusterLayer from "@/components/VillageIconLayer/components/ClusterLayer"
import BranchAoi from "./components/BranchAoi"
// import BranchHeat from "./components/BranchHeat"
import BusinessCircle from "./components/BusinessCircle"
import BrandPanel from "./components/Panel"
import ScorePanel from "./components/ScorePanel"
import CityLocation from "./components/CityLocation"
import STORE_ATLAS from "./img/store-atlas.png"
import "./index.scss"
// ==============================================
const ICON_MAPPING = {
  S: { x: 0, y: 0, width: 72, height: 68 },
  A: { x: 72, y: 0, width: 72, height: 68 },
  B: { x: 144, y: 0, width: 72, height: 68 },
  C: { x: 216, y: 0, width: 72, height: 68 },
  D: { x: 288, y: 0, width: 72, height: 68 },
}

const ICON_SIZE = 20

const score = scaleQuantize()
  .domain([0, 10])
  .range(["D", "C", "B", "A", "S"] as any) as unknown as any

const showStorePage = [
  "analysis",
  "indicator",
  "eco",
  "mall",
  "starbucksSelection",
  "relatively",
  "insights",
]

const showPanel = ["indicator", "analysis", "eco", "insights"]

type IndustryPointProps = {
  color?: string
  visible?: boolean
  // 控制已开通/未开通网点的显隐
  branchVisible: BranchShowVO
  onClick?: (record: any) => void
  random?: boolean
}

type IndustryPointState = Readonly<{
  activeCode?: string
  viewState?: any
  // townshipFeatureCollection?: any
  storeFeatureCollection?: any
  unlockedStoreFeatureCollection?: any
  lockedStoreFeatureCollection?: any
  textCharacterSet?: any
  showType: "CITY" | "DISTRICT" | "TOWNSHIP" | "STORE"
  zoom: number
  HoveredStore?: any
}>

const initialState: IndustryPointState = {
  storeFeatureCollection: null,
  unlockedStoreFeatureCollection: null,
  lockedStoreFeatureCollection: null,
  showType: "CITY",
  zoom: 3,
}

const IndustryPoint: FC<IndustryPointProps> = ({ visible = false, branchVisible }) => {
  const location = useLocation()
  const { getViewState, setViewState, setHoverInfo } = useMapGLContext()
  const { emitter } = useMapValue()
  const { sandTableId, cityId, brandId } = useParams<RouterIdsVO>() as any
  const { storeType, storeList, setStoreList, setBrandStoreCount } = useStoreListModel()
  const { brandPoi, setBrandPoiValue } = useBrandPoiModel()
  const { getQuota, openQuota, cacheQuota } = useQuotaToolsModel()
  const { currentConfig, industryCategoryType } = useConfigModel()
  const { theme, isAllShow } = useControllerModel()
  const { screeningData, setAllState: setIndicatorAllState } = useIndicatorModel()
  const [state, setState] = useSetState<IndustryPointState>(initialState)
  const {
    storeFeatureCollection,
    unlockedStoreFeatureCollection,
    lockedStoreFeatureCollection,
    textCharacterSet,
    showType,
    zoom,
    HoveredStore,
  } = state
  // const { outletscode, outletstype, outletscategory, stadate } = screeningData || {}

  const type = getCurrentPageType(location.pathname)

  // 获取网点主题颜色
  const colorTheme = useCreation(() => getBranchThemeColors(), [theme])
  const {
    iconStrokeColor,
    iconTextColor,
    iconTextBackgroundColor,
    iconTextColorHover,
    iconTextBackgroundColorHover,
  } = colorTheme

  // Panel组件是否显示
  const isShowPanel = useCreation(() => {
    return showPanel.includes(type)
  }, [type])

  // 需要显示的网点数据
  const storeData = useCreation(() => {
    const { lockedShow, unlockedShow } = branchVisible
    if (lockedShow && unlockedShow) {
      return storeFeatureCollection?.features || []
    }

    if (lockedShow) {
      return lockedStoreFeatureCollection?.features || []
    }

    if (unlockedShow) {
      return unlockedStoreFeatureCollection?.features || []
    }

    return []
  }, [
    branchVisible,
    storeFeatureCollection,
    unlockedStoreFeatureCollection,
    lockedStoreFeatureCollection,
  ])

  // 需要显示的网点数据
  const filterStoreList = useCreation(() => {
    const { lockedShow, unlockedShow } = branchVisible
    const list = storeList || []

    if (lockedShow && unlockedShow) {
      return list
    }
    if (lockedShow) {
      return list.filter((store) => !store.unlocked)
    }
    if (unlockedShow) {
      return list.filter((store) => store.unlocked)
    }

    return []
  }, [branchVisible, storeList])

  // 显示icon的text
  const showTextZoom = useCreation(() => {
    if (industryCategoryType === IndustryCategory.ADVERTISEMENT) {
      return SHOW_ADVERTISEMENT_TEXT
    } else {
      return SHOW_ICON_TEXT
    }
  }, [industryCategoryType])

  const getIcon = useCallback((d) => {
    // 根据客流评分, 获取Icon
    return score(d.properties.flowRatio ?? 0)
  }, [])

  // 设置缓存网点
  const changeStore = usePersistFn((poi: any, isLocation = true) => {
    // 缓存单网点
    cacheQuota(poi)
    // 定位
    if (isLocation) {
      const { subtract } = getCenterOffset(getViewState, viewZoom)
      // 切换到点
      setViewState({
        longitude: isAllShow ? poi.lng + subtract[0] : poi.lng,
        latitude: poi.lat,
        zoom: viewZoom,
      })
    }
  })

  // 品牌在所有城市的未开通和已开通总数
  const { data: countData } = useRequest(() => getBrandStoreCount(brandId), {
    ready: !!brandId,
    refreshDeps: [brandId],
  })

  useEffect(() => {
    const { lockedCount = 0, unlockedCount = 0 } = countData || {}

    if (storeType === TYPE_WAYZ_POI) {
      // 设置品牌已开未开网点数
      setBrandStoreCount({
        unlockedCount,
        lockedCount,
      })
    }
  }, [countData, storeType, setBrandStoreCount])

  // 获取网点
  const { run: getList } = useRequest(
    (filterData?: BranchFilters) =>
      getStoreList({
        ...filterData,
        brandId,
        cityId,
        isAsc: false,
        limit: storeLimit,
        // moduleType: getMenuKeyByPath(type),
        page: 0,
        scale: 2000,
      }),
    {
      manual: true,
      refreshDeps: [brandId, cityId],
      onSuccess: (result) => {
        const lastItem = result?.slice(-1)?.[0]
        if (storeType === TYPE_WAYZ_POI) {
          setStoreList(result)
        }
        // 如果缓存网点能够在新列表中找到，则不清除缓存网点
        const activeBrand = result?.find((store: any) => store.poiId === brandPoi?.poiId)
        if (!currentConfig?.singleStoreAuth) {
          if (!(brandPoi && activeBrand)) {
            // 清除缓存网点
            setBrandPoiValue(null)
          }
        }
        // 经营情况页面，若支行只有一个网点则缓存网点
        if (
          type === "indicator" &&
          screeningData?.outletscode &&
          result?.length === 1 &&
          lastItem &&
          screeningData?.outletscode === lastItem?.outletscode &&
          lastItem?.unlocked
        ) {
          const { longitude, latitude } = lastItem
          // 设置缓存网点
          changeStore(
            {
              ...lastItem,
              lng: longitude,
              lat: latitude,
            },
            false,
          )
        }
      },
      onError: () => {
        // 清除缓存网点
        setBrandPoiValue(null)
        if (storeType === TYPE_WAYZ_POI) {
          setStoreList([])
        }
      },
    },
  )

  // 获取网点
  useEffect(() => {
    getList()
  }, [getList, brandId, cityId])

  // 单网点权限
  useEffect(() => {
    if (storeList?.length && currentConfig && currentConfig.singleStoreAuth) {
      const storeUnlockedList =
        storeList?.filter((item) => item.unlocked && (item as any).type !== TYPE_CUSTOM_POI) || []
      // 自定义网点不用做单网点
      if (storeUnlockedList?.length) {
        const store = storeUnlockedList[0]
        const { longitude, latitude, outletscode } = store
        if (store.poiId !== brandPoi?.poiId) {
          // 设置缓存网点
          changeStore({
            ...store,
            lng: longitude,
            lat: latitude,
          })
          if (isNH && type === "indicator") {
            // 缓存筛选条件
            setIndicatorAllState((prev) => ({
              screeningData: {
                ...prev.screeningData,
                outletscode,
              },
            }))
          }
        }
      }
    }
  }, [type, brandPoi, currentConfig, storeList, changeStore, setIndicatorAllState])

  // 获取单网点额度
  useEffect(() => {
    ;(async () => {
      try {
        if (currentConfig) {
          const { singleAuth } = currentConfig
          await getQuota(cityId, brandId, singleAuth)
        }
      } catch (error) {
        console.warn("🚀 ~ file: index.tsx ~ line 199 ~ ; ~ error", error)
      }
    })()
  }, [cityId, brandId, getQuota, currentConfig])

  // 监听viewState change
  useEffect(() => {
    const token = emitter.addListener("viewStateChange", async (viewState: any) => {
      const { zoom: viewZoom } = viewState
      const zoom = viewZoom || 1
      const showType: any =
        zoom >= SHOW_SINGLE_STORE_ZOOM ? "STORE" : zoom <= showZoomMap.CITY ? "CITY" : "DISTRICT"
      // 防止缩放时, 提示框不消失
      if (showType === "STORE") {
        setHoverInfo(null as any)
      }

      // Analysis城市边界使用, 是否高亮当前区域
      emitter.emit("showTypeChange", showType)

      const state = { zoom }

      setState((prevState) => {
        // showType
        if (prevState.showType !== showType) {
          Object.assign(state, { showType })
        }

        return state
      })
    })
    return () => {
      token.remove()
    }
  }, [emitter, isAllShow, setState, setHoverInfo])

  // 点击网点时
  const handleIconLayerClick = usePersistFn(({ object }) => {
    const { geometry, properties } = object
    const [longitude, latitude] = geometry.coordinates
    const store = Object.assign(properties, {
      longitude,
      latitude,
      lng: longitude,
      lat: latitude,
    })
    const { unlocked } = store
    if (!unlocked) {
      // 开通单网点
      openQuota(store, cityId, brandId, currentConfig?.singleAuth, false, (result) => {
        if (result) {
          // 获取网点
          getList().finally(() => changeStore(store))
        }
      })
      return
    }
    changeStore(store)
  })

  const handleIconLayerHover = usePersistFn(({ object }) => {
    if (object) {
      if (HoveredStore) {
        const { poiId } = object.properties
        const { poiId: id } = [HoveredStore][0]
        if (poiId === id) return
      }
      setState({ HoveredStore: [object] })
    }
  })

  const handleHoveredIconLayerClick = usePersistFn(({ picked }) => {
    if (!picked) setState({ HoveredStore: undefined })
  })

  // 点击城市边界回调
  const cityGeoJsonLayerCallback = usePersistFn(() => {
    // 切换数据显示
    setState((prevState) => {
      return {
        ...prevState,
        // showType: "DISTRICT",
        showType: "CITY",
      }
    })
  })

  // 点击区县边界回调
  const districtGeoJsonLayerCallback = usePersistFn(() => {
    // 切换数据显示
    setState((prevState) => {
      return {
        ...prevState,
        showType: "STORE",
      }
    })
  })

  // 聚合回调
  const onVillageIconClick = usePersistFn((data) => {
    const { longitude, latitude } = data

    unstable_batchedUpdates(() => {
      // 地图定位缩放
      setViewState({
        longitude: longitude + 0.005,
        latitude,
        zoom: viewZoom,
      })
      // 切换数据显示
      setState((prevState) => {
        return {
          ...prevState,
          showType: "STORE",
        }
      })
    })
  })

  // 网点数据
  useEffect(() => {
    if (!storeList) return

    const numberStr = "0123456789"
    const textCharacterSet = new Set()
    numberStr.split("").forEach((item: any) => textCharacterSet.add(item))
    storeList.forEach((item: any) => {
      // 网点颜色
      item.poiName.split("").forEach((char: any) => textCharacterSet.add(char))
    })

    const storeFeatureCollection = GeoJSON.parse(storeList || [], {
      Point: ["latitude", "longitude"],
    })
    const unlockedStore: any = [],
      lockedStore: any = []
    storeList.forEach((item: any) => {
      item.unlocked ? unlockedStore.push(item) : lockedStore.push(item)
    })
    const unlockedStoreFeatureCollection = GeoJSON.parse(unlockedStore, {
      Point: ["latitude", "longitude"],
    })
    const lockedStoreFeatureCollection = GeoJSON.parse(lockedStore, {
      Point: ["latitude", "longitude"],
    })

    setState({
      unlockedStoreFeatureCollection,
      lockedStoreFeatureCollection,
      storeFeatureCollection,
      textCharacterSet,
    })
  }, [storeList, setState])

  const showStore = showStorePage.includes(type) && showType === "STORE"

  const isDemo = useCreation(() => {
    return type === "starbucksSelection" || type === "relatively"
  }, [type])

  return (
    <>
      {/* 网点icon */}
      <GeoJsonLayer
        id="labeled-geojson-store-layer"
        visible={visible && showStore && storeData.length > 0}
        data={storeData}
        pickable={true}
        autoHighlight={false}
        pointType={zoom >= showTextZoom ? "icon+text" : "icon"}
        // @ts-expect-error
        billboard={false}
        highlightColor={colorToRGBArray("rgba(40, 128, 255, 0.8)")}
        getIcon={getIcon}
        iconAtlas={STORE_ATLAS}
        iconMapping={ICON_MAPPING}
        iconBillboard={false}
        getIconSize={ICON_SIZE}
        textCharacterSet={textCharacterSet}
        textWordBreak="break-all"
        getTextAnchor="start"
        getTextPixelOffset={[20, -5]}
        getText={(f: any) => `${f.properties.poiName}`}
        getTextSize={12}
        getTextColor={colorToRGBArray(iconTextColor)}
        textSizeUnits="pixels"
        textFontFamily="Microsoft YaHei"
        getTextBorderColor={colorToRGBArray(iconStrokeColor)}
        getTextBorderWidth={1}
        textBackground={true}
        textBackgroundPadding={[5, 5]}
        getTextBackgroundColor={colorToRGBArray(iconTextBackgroundColor)}
        onClick={handleIconLayerClick}
        onHover={handleIconLayerHover}
        zIndex={10}
        updateTriggers={{ getIcon }}
      />

      {/* 网点active icon */}
      <GeoJsonLayer
        id="labeled-geojson-hover-store-layer"
        visible={visible && HoveredStore && showStore}
        data={HoveredStore}
        pickable={true}
        autoHighlight={false}
        pointType="icon+text"
        // @ts-expect-error
        billboard={false}
        getIcon={getIcon}
        iconAtlas={STORE_ATLAS}
        iconMapping={ICON_MAPPING}
        iconBillboard={false}
        getIconSize={ICON_SIZE}
        textCharacterSet={textCharacterSet}
        textWordBreak="break-all"
        getTextAnchor="start"
        getTextPixelOffset={[20, -5]}
        getText={(f: any) => `${f.properties.poiName}`}
        getTextSize={12}
        getTextColor={colorToRGBArray(iconTextColorHover)}
        textSizeUnits="pixels"
        textFontFamily="Microsoft YaHei"
        getTextBorderColor={colorToRGBArray(iconStrokeColor)}
        getTextBorderWidth={0}
        textBackground={true}
        textBackgroundPadding={[5, 5]}
        getTextBackgroundColor={colorToRGBArray(iconTextBackgroundColorHover)}
        onClick={handleIconLayerClick}
        zIndex={11}
        updateTriggers={{ getIcon }}
        onHover={handleHoveredIconLayerClick}
      />

      {isDemo ? (
        <>
          {/* 网点聚合效果 */}
          <ClusterLayer
            id="store"
            visible={showType !== "STORE"}
            data={filterStoreList}
            fillColor={colorTheme.fillColor}
            textColor={colorTheme.unlockedColor}
            borderColor={colorTheme.unlockedColor}
            onVillageIconClick={onVillageIconClick}
          />
          {/* 城市定位 */}
          <CityLocation />
        </>
      ) : (
        // 城市边界+区县边界
        // <BranchHeat
        //   sandTableId={sandTableId}
        //   cityId={cityId}
        //   brandId={brandId}
        //   visible={visible}
        //   type={type}
        //   showType={showType}
        //   districtGeoJsonLayerCallback={districtGeoJsonLayerCallback}
        //   cityGeoJsonLayerCallback={cityGeoJsonLayerCallback}
        // />
        // 区域热力
        <AreaHeat
          visible={visible}
          sandTableId={sandTableId}
          cityId={cityId}
          brandId={brandId}
          showType={showType}
          districtGeoJsonLayerCallback={districtGeoJsonLayerCallback}
          cityGeoJsonLayerCallback={cityGeoJsonLayerCallback}
        />
      )}

      {/* 选址评分 */}
      {showStore && <ScorePanel />}

      {/* 网点的polygon */}
      <BranchAoi
        visible={visible && showType === "STORE" && storeData.length}
        storeList={storeList}
        unlockedShow={branchVisible.unlockedShow}
        lockedShow={branchVisible.lockedShow}
      />

      {/* 商圈 */}
      {isDemo && <BusinessCircle visible={isDemo} />}

      {/* 左下角点类型列表 */}
      <BrandPanel
        brandName={currentConfig?.brandName}
        zoom={zoom}
        legendColors={polygonHeatColors}
        visible={isShowPanel}
      />
    </>
  )
}

export default memo(IndustryPoint)
